/* eslint-disable sonarjs/cognitive-complexity */
// 9fbef606107a605d69c0edbcd8029e5d
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { HorizontalScroll } from '@tcp/core/src/components/features/browse/HorizontalCategory/molecules/HorizontalCarousel/HorizontalCarousel.style';
import {
  StyledHorizontalCarousel,
  HorizontalGrid,
} from '../styles/TrendingInCategoryDesktopCarousel.style';

const useIntersectionObserver = (options = {}, defaultVisibility = false) => {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(defaultVisibility);

  const callbackFn = (entries) => {
    const [entry] = entries;
    setIsVisible(entry.isIntersecting);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(callbackFn, options);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
    };
  }, [containerRef?.current?.getBoundingClientRect(), options]);

  return [containerRef, isVisible];
};

const TrendingInCategoryDesktopCarousel = ({
  children,
  isArrowVisible,
  tileRef,
  defaultTileWidth,
  slidesToScroll,
}) => {
  const scrollContainerRef = useRef(null);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [scrollEndlRef, isEndTileInView] = useIntersectionObserver({
    root: null,
    rootMargin: '0px',
  });
  const [scrolStartRef, isStartTileInView] = useIntersectionObserver({
    root: null,
    rootMargin: '0px',
  });

  useEffect(() => {
    if (isStartTileInView && tileRef) {
      setShowLeftArrow(false);
      setShowRightArrow(true);
    } else {
      setShowLeftArrow(true);
    }
  }, [isStartTileInView]);

  useEffect(() => {
    if (isEndTileInView && tileRef) {
      setShowRightArrow(false);
      setShowLeftArrow(true);
    } else {
      setShowRightArrow(true);
    }
  }, [isEndTileInView]);

  useEffect(() => {
    if (isStartTileInView && isEndTileInView && tileRef) {
      setShowRightArrow(false);
      setShowLeftArrow(false);
    }
  }, [isStartTileInView, isEndTileInView]);

  const onScrollNext = useCallback(() => {
    const oneTileWidth = tileRef?.current?.offsetWidth || defaultTileWidth;
    if (scrollContainerRef) {
      scrollContainerRef.current.scrollLeft += slidesToScroll * oneTileWidth;
    }
  });

  const onScrollPrev = useCallback(() => {
    const oneTileWidth = tileRef?.current?.offsetWidth || defaultTileWidth;
    if (scrollContainerRef) {
      scrollContainerRef.current.scrollLeft -= oneTileWidth * slidesToScroll;
    }
  });

  return (
    <>
      {showLeftArrow && isArrowVisible ? (
        <button onClick={onScrollPrev} className="carousel-arrows carousel-left-arrow">
          <div className="left-arrow" />
        </button>
      ) : null}
      <StyledHorizontalCarousel>
        <HorizontalScroll ref={scrollContainerRef}>
          <HorizontalGrid className="carousel-content">
            <div ref={scrolStartRef} />
            {children}
            <div ref={scrollEndlRef} />
          </HorizontalGrid>
        </HorizontalScroll>
      </StyledHorizontalCarousel>
      {showRightArrow && isArrowVisible ? (
        <button onClick={onScrollNext} className="carousel-arrows carousel-right-arrow">
          <div className="right-arrow" />
        </button>
      ) : null}
    </>
  );
};

TrendingInCategoryDesktopCarousel.propTypes = {
  children: PropTypes.node,
  isArrowVisible: PropTypes.bool,
  tileRef: PropTypes.node,
  slidesToScroll: PropTypes.number,
  defaultTileWidth: PropTypes.number,
};

TrendingInCategoryDesktopCarousel.defaultProps = {
  children: null,
  isArrowVisible: false,
  tileRef: null,
  slidesToScroll: 3,
  defaultTileWidth: 283,
};

export default TrendingInCategoryDesktopCarousel;
